/**
 * GET /api/user · registrationFormGrants / resourceGrantPermissionNames
 * ACL 一次授权后持久有效；普通登录亦应返回全量 grant
 */

function pickField (obj, ...keys) {
  if (!obj || typeof obj !== 'object') return undefined
  for (const k of keys) {
    const v = obj[k]
    if (v !== undefined && v !== null && v !== '') return v
  }
  return undefined
}

/** 归一化单条 registrationFormGrant */
export function normalizeRegistrationFormGrant (raw) {
  if (!raw || typeof raw !== 'object') return null
  const registrationIdRaw = pickField(raw, 'registrationId', 'registration_id')
  const formIdRaw = pickField(raw, 'formId', 'form_id')
  const registrationId = registrationIdRaw != null ? String(registrationIdRaw).trim() : ''
  const formId = formIdRaw != null ? String(formIdRaw).trim() : (registrationId || '')
  if (!formId && !registrationId) return null
  const companyIdRaw = pickField(raw, 'companyId', 'company_id')
  const companyId = Number(companyIdRaw)
  const permRaw = raw.permissionNames ?? raw.permission_names
  const permissionNames = Array.isArray(permRaw)
    ? [...new Set(permRaw.map((n) => String(n).trim()).filter(Boolean))]
    : []
  return {
    formId,
    registrationId: registrationId || (/^\d+$/.test(formId) ? formId : ''),
    companyId: Number.isInteger(companyId) && companyId > 0 ? companyId : null,
    permissionNames,
    grantSource: pickField(raw, 'grantSource', 'grant_source') || null
  }
}

/** 从 user / GET /user 解包对象读取表单级 grant 列表 */
export function getRegistrationFormGrantsFromUser (user) {
  const raw = user?.registrationFormGrants ?? user?.registration_form_grants
  if (!Array.isArray(raw)) return []
  return raw.map(normalizeRegistrationFormGrant).filter(Boolean)
}

/** 从 registrationFormGrants 汇总全部 ACL 叶子权限名 */
export function collectPermissionNamesFromFormGrants (grants) {
  const names = []
  for (const g of grants || []) {
    for (const n of g?.permissionNames || []) {
      const s = String(n).trim()
      if (s) names.push(s)
    }
  }
  return [...new Set(names)]
}

/**
 * 默认 grant 租户 companyId（展示/调试；定稿后个人模式 API 不带 X-Company-Id）
 * 优先 registrationFormGrants[0].companyId
 */
export function resolveGrantCompanyIdFromUser (user) {
  const grants = getRegistrationFormGrantsFromUser(user)
  for (const g of grants) {
    if (g.companyId != null) return g.companyId
  }
  const companies = user?.companies
  if (Array.isArray(companies)) {
    for (const c of companies) {
      const id = Number(c?.id)
      if (Number.isInteger(id) && id > 0) return id
    }
  }
  const single = user?.company
  if (single && typeof single === 'object' && single.id != null) {
    const id = Number(single.id)
    if (Number.isInteger(id) && id > 0) return id
  }
  return null
}

/** 按 registrationId / formId 查表单级 grant（表单页 Tab 可叠加 permissionNames） */
export function findRegistrationFormGrant (user, registrationOrFormId) {
  const key = String(registrationOrFormId || '').trim()
  if (!key) return null
  return getRegistrationFormGrantsFromUser(user).find((g) =>
    g.registrationId === key || g.formId === key
  ) || null
}

/** 是否为 ACL 协作者（GET /user · is_acl_collaborator 或仅有 grant 无成员公司） */
export function isAclCollaboratorUser (user) {
  if (!user || typeof user !== 'object') return false
  if (user.is_acl_collaborator === true || user.isAclCollaborator === true) return true
  const companies = user.companies
  if (Array.isArray(companies) && companies.length > 0) return false
  const grants = getRegistrationFormGrantsFromUser(user)
  const raw = user.resourceGrantPermissionNames ?? user.resource_grant_permission_names
  return grants.length > 0 || (Array.isArray(raw) && raw.length > 0)
}

/** resourceGrantPermissionNames 叶子名 */
function getResourceGrantNamesFromUser (user) {
  const raw = user?.resourceGrantPermissionNames ?? user?.resource_grant_permission_names
  if (!Array.isArray(raw)) return []
  return [...new Set(raw.map((n) => String(n).trim()).filter(Boolean))]
}

/**
 * 报名表单编辑页有效权限
 * - ACL 协作者且命中 form grant：仅用 grant ∪ resourceGrant（不含个人 Spatie 里的「编辑报名表单」）
 * - 其他：返回 null，由调用方走 getEffectivePermissionNames
 */
export function resolveRegistrationFormEditorPermissionNames (user, formId, formEffectivePermissions = []) {
  if (!isAclCollaboratorUser(user)) return null
  const grant = findRegistrationFormGrant(user, formId)
  if (!grant) return null

  const aclNames = [...new Set([
    ...getResourceGrantNamesFromUser(user),
    ...grant.permissionNames
  ])]
  const formPerms = Array.isArray(formEffectivePermissions)
    ? formEffectivePermissions.map((n) => String(n).trim()).filter(Boolean)
    : []
  const filteredForm = formPerms.filter((n) => aclNames.includes(n))
  return [...new Set([...aclNames, ...filteredForm])]
}

/** 从 localStorage.user 读取默认 grant companyId */
export function resolveGrantCompanyIdFromStorage () {
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return null
    return resolveGrantCompanyIdFromUser(JSON.parse(raw))
  } catch {
    return null
  }
}
